/**
 * Collapsible Sidebar Web Component
 * Docker-style sidebar that expands/collapses with persistent state.
 * Icons only when collapsed, icons + labels when expanded.
 *
 * Precondition: None
 * Postcondition: Renders a fixed sidebar with expand/collapse toggle
 * Invariants: Width transitions smoothly, state persists to localStorage
 */

import { icon } from '../../../shared/icons.js';

const STORAGE_KEY = 'ft-sidebar-expanded';

class CollapsibleSidebar extends HTMLElement {
  private _expanded: boolean = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['expanded'];
  }

  connectedCallback(): void {
    // Restore state from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    this._expanded = stored === 'true';
    if (this._expanded) {
      this.setAttribute('expanded', '');
    }

    this.render();
    this.setupEventListeners();
    this.updateMainMargin();
  }

  disconnectedCallback(): void {
    document.body.style.marginLeft = '';
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'expanded' && oldValue !== newValue) {
      this._expanded = newValue !== null;
      this.updateVisualState();
      this.updateMainMargin();
      localStorage.setItem(STORAGE_KEY, String(this._expanded));
    }
  }

  get expanded(): boolean {
    return this._expanded;
  }

  set expanded(value: boolean) {
    if (value) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
  }

  toggle(): void {
    this.expanded = !this.expanded;
    this.dispatchEvent(new CustomEvent(this.expanded ? 'sidebar-expand' : 'sidebar-collapse', {
      bubbles: true,
      composed: true
    }));
  }

  expand(): void {
    this.expanded = true;
    this.dispatchEvent(new CustomEvent('sidebar-expand', { bubbles: true, composed: true }));
  }

  collapse(): void {
    this.expanded = false;
    this.dispatchEvent(new CustomEvent('sidebar-collapse', { bubbles: true, composed: true }));
  }

  private updateVisualState(): void {
    const nav = this.shadowRoot?.querySelector('nav');
    if (nav) {
      if (this._expanded) {
        nav.classList.add('expanded');
      } else {
        nav.classList.remove('expanded');
      }
    }
  }

  private updateMainMargin(): void {
    const collapsedWidth = 64;
    const expandedWidth = 240;
    document.body.style.marginLeft = `${this._expanded ? expandedWidth : collapsedWidth}px`;
  }

  private render(): void {
    const style = document.createElement('style');
    style.textContent = this.getStyles();

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    // Apply initial state
    this.updateVisualState();
  }

  private getStyles(): string {
    return `
      :host {
        --collapsed-width: 64px;
        --expanded-width: 240px;
        --sidebar-bg: var(--surface, #14161a);
        --sidebar-border: var(--divider-color, #2a2d33);
        --text-primary: var(--text, #ffffff);
        --text-secondary: var(--muted, #a3a3a3);
        --hover-bg: var(--tab-active-bg, #1a1c22);
        --active-bg: var(--tab-active-bg, #1a1c22);
        --transition-duration: 200ms;
        --transition-easing: cubic-bezier(0.3, 0.7, 0.4, 1);

        display: block;
        position: fixed;
        top: 0;
        left: 0;
        height: 100vh;
        z-index: 1000;
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      nav {
        width: var(--collapsed-width);
        height: 100%;
        background: var(--sidebar-bg);
        border-right: 1px solid var(--sidebar-border);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        will-change: width;
        transition: width var(--transition-duration) var(--transition-easing);
      }

      nav.expanded {
        width: var(--expanded-width);
      }

      /* Header with toggle button */
      .sidebar-header {
        display: flex;
        align-items: center;
        height: 56px;
        padding: 0 8px;
        border-bottom: 1px solid var(--sidebar-border);
        flex-shrink: 0;
      }

      .toggle-btn {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        color: var(--text-secondary);
        transition:
          background 150ms ease-out,
          color 150ms ease-out;
        -webkit-tap-highlight-color: transparent;
        flex-shrink: 0;
      }

      .toggle-btn:hover {
        background: var(--hover-bg);
        color: var(--text-primary);
      }

      .toggle-btn:focus-visible {
        outline: 2px solid var(--text-primary);
        outline-offset: -2px;
      }

      .toggle-btn:active {
        transform: scale(0.98);
      }

      .toggle-btn svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
        transition: transform var(--transition-duration) var(--transition-easing);
      }

      nav.expanded .toggle-btn svg {
        transform: rotate(180deg);
      }

      /* Logo/brand area - appears when expanded */
      .brand {
        display: flex;
        align-items: center;
        gap: 12px;
        padding-left: 12px;
        opacity: 0;
        width: 0;
        overflow: hidden;
        white-space: nowrap;
        transition:
          opacity var(--transition-duration) var(--transition-easing),
          width var(--transition-duration) var(--transition-easing);
      }

      nav.expanded .brand {
        opacity: 1;
        width: auto;
      }

      .brand-text {
        font: 600 15px/1 var(--font-sans, Inter, system-ui, sans-serif);
        color: var(--text-primary);
        letter-spacing: -0.01em;
      }

      /* Navigation content */
      .sidebar-content {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 8px;
      }

      /* Footer slot */
      .sidebar-footer {
        padding: 8px;
        border-top: 1px solid var(--sidebar-border);
        flex-shrink: 0;
      }

      /* Slotted content styling */
      ::slotted(*) {
        display: block;
      }

      /* Reduced motion */
      @media (prefers-reduced-motion: reduce) {
        nav,
        .toggle-btn svg,
        .brand {
          transition: none;
        }
      }
    `;
  }

  private getTemplate(): string {
    // Chevron icon pointing right (rotates 180° when expanded)
    return `
      <nav${this._expanded ? ' class="expanded"' : ''}>
        <div class="sidebar-header">
          <button class="toggle-btn" aria-label="Toggle sidebar" aria-expanded="${this._expanded}">
            ${icon('chevron-right', 20)}
          </button>
          <div class="brand">
            <span class="brand-text"><slot name="brand">FormThirteen</slot></span>
          </div>
        </div>
        <div class="sidebar-content">
          <slot></slot>
        </div>
        <div class="sidebar-footer">
          <slot name="footer"></slot>
        </div>
      </nav>
    `;
  }

  private setupEventListeners(): void {
    const toggleBtn = this.shadowRoot?.querySelector('.toggle-btn');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => this.toggle());
    }
  }
}

if (!customElements.get('ft-collapsible-sidebar')) {
  customElements.define('ft-collapsible-sidebar', CollapsibleSidebar);
}
